<script setup lang="ts">
import { computed, ref } from "vue";

interface Props {
  size?: "small" | "large";
  disabled?: boolean;
  selected: boolean;
  tabbable: boolean;
  index: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: "large",
  disabled: false
});

const emit = defineEmits<{
  (event: "blur", index: number): void;
  (event: "focusNext", index: number): void;
  (event: "focusPrev", index: number): void;
  (event: "select", index: number): void;
}>();

const COMPONENT_CLASS = "le-selector-button";

const tabIndex = computed<string>(() => (props.tabbable ? "0" : "-1"));

const classes = computed<string[]>(() => [
  COMPONENT_CLASS,
  `${COMPONENT_CLASS}--size-${props.size}`,
  props.selected ? `${COMPONENT_CLASS}--selected` : ""
]);

const input = ref<HTMLElement>();

function onFocus() {
  input.value?.focus();
}

function onInputBlur() {
  emit("blur", props.index);
}

function onArrowKey(e: KeyboardEvent) {
  switch (e.code) {
    case "ArrowLeft":
    case "ArrowUp":
      emit("focusPrev", props.index);
      break;

    case "ArrowRight":
    case "ArrowDown":
      emit("focusNext", props.index);
      break;

    default:
      break;
  }
}

function onClick() {
  if (props.disabled) {
    return;
  }

  emit("select", props.index);
  onFocus();
}
</script>

<template>
  <button
    ref="input"
    :class="classes"
    type="button"
    role="radio"
    :tabindex="tabIndex"
    :aria-checked="selected"
    :disabled="disabled"
    @blur.prevent="onInputBlur()"
    @click.prevent="onClick()"
    @keydown.left.right.up.down.prevent="onArrowKey($event)"
  >
    <slot />
  </button>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-selector-button {
  border: 1px solid @colors[@secondary][600];
  border-radius: 4px;
  background: @colors[@neutrals][100];
  .body-text("s", "bold");
  color: @colors[@secondary][800];
  text-align: center;

  &:disabled {
    opacity: 0.3;
  }

  &:hover:not(:disabled) {
    border: 1px solid @colors[@secondary][600];
    background: @colors[@secondary][200];
  }

  &.focus-visible {
    border: 1px solid @colors[@secondary][600];
    .focus-outline(grey);
  }

  &--size-small {
    padding: 4px 8px;
  }

  &--size-large {
    padding: 10px 16px;
  }

  &--selected {
    border: 1px solid @colors[@accent][700];
    background: @colors[@accent][300];
    color: @colors[@neutrals][400];

    &:hover:not(:disabled) {
      background: @colors[@accent][400];
    }

    &.focus-visible {
      .focus-outline(yellow);
    }
  }
}
</style>
